import { useEffect } from "react";
import { SITE_URL } from "@/lib/siteUrl";

/**
 * Lightweight SEO hook — sets document.title + meta tags + canonical + JSON-LD.
 * Works for client-rendered React SPAs; modern search engines (Google, Bing) and
 * AI crawlers (GPTBot, PerplexityBot, ClaudeBot) execute JS and read these.
 */
export default function useSEO({
  title,
  description,
  canonical,
  ogImage = "https://images.unsplash.com/photo-1518800524495-b963b722bd92?w=1200&q=80",
  ogType = "website",
  noindex = false,
  jsonLd = null,
  keywords = null,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title}` : "Ecowallet – AI Sustainability Scores & Rewards";
    document.title = fullTitle;

    const setMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    if (title) {
      setMeta("property", "og:title", fullTitle);
      setMeta("name", "twitter:title", fullTitle);
    }
    if (keywords) setMeta("name", "keywords", keywords);
    setMeta("property", "og:image", ogImage);
    setMeta("name", "twitter:image", ogImage);
    setMeta("property", "og:type", ogType);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "robots", noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1");

    // Canonical — always points to the primary domain regardless of the host
    // used to reach the page (preview URL, www, apex, etc.), so search engines
    // consolidate signals on ecowallet.in.
    let canonicalUrl = canonical;
    if (!canonicalUrl && typeof window !== "undefined") {
      canonicalUrl = SITE_URL + window.location.pathname;
    }
    if (canonicalUrl) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonicalUrl);
      setMeta("property", "og:url", canonicalUrl);
    }

    // Page-specific JSON-LD
    const scriptId = "page-jsonld";
    let script = document.getElementById(scriptId);
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = scriptId;
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, [title, description, canonical, ogImage, ogType, noindex, JSON.stringify(jsonLd), keywords]);
}
